import { isAllowedImageSource } from "./imageSource";

const PAGE_WIDTH = 700;
const PAGE_HEIGHT = 990;
const DEFAULT_FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_FAMILIES = new Set([
    DEFAULT_FONT_FAMILY,
    "Arial, sans-serif",
    "Georgia, serif",
    "Times New Roman, serif",
    "Verdana, sans-serif"
]);
const PAGE_TEXTURES = new Set(["plain", "dots", "lines", "diagonal"]);

const escapeHtml = value => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeJsonForHtml = value => JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

const safeNumber = (value, fallback, maximum) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(maximum, Math.max(0, number));
};

const safeColor = (value, fallback) => {
    if (value === "transparent") return value;
    return typeof value === "string" && /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i.test(value)
        ? value
        : fallback;
};

const normalizeDocument = document => ({
    schemaVersion: 1,
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    pageStyle: {
        backgroundColor: safeColor(document?.pageStyle?.backgroundColor, "#FFFFFF"),
        texture: PAGE_TEXTURES.has(document?.pageStyle?.texture) ? document.pageStyle.texture : "plain"
    },
    elements: (Array.isArray(document?.elements) ? document.elements : []).map(sourceElement => {
        const element = { ...sourceElement };
        delete element.imageLoadError;
        return {
            ...element,
            x: safeNumber(element.x, 0, PAGE_WIDTH),
            y: safeNumber(element.y, 0, PAGE_HEIGHT),
            width: Math.max(1, safeNumber(element.width, 1, PAGE_WIDTH)),
            height: Math.max(1, safeNumber(element.height, 1, PAGE_HEIGHT))
        };
    })
});

const elementStyle = (element, layerIndex) => {
    const isShape = element.type === "rectangle" || element.type === "ellipse";
    const isLine = element.type === "line";
    const styles = [
        "position:absolute",
        "display:flex",
        "align-items:center",
        "box-sizing:border-box",
        "margin:0",
        "overflow:visible",
        "border:1px solid transparent",
        `z-index:${layerIndex + 1}`,
        `left:${element.x}px`,
        `top:${element.y}px`,
        `width:${element.width}px`,
        `height:${element.height}px`,
        `padding:${isShape || isLine ? 0 : 8}px`,
        `color:${safeColor(element.color, "#1F2937")}`,
        `background:${safeColor(element.background, "transparent")}`,
        `font-size:${safeNumber(element.fontSize, 16, 96)}px`,
        `font-weight:${element.fontWeight === "bold" ? "bold" : "normal"}`,
        `font-style:${element.fontStyle === "italic" ? "italic" : "normal"}`,
        `font-family:${FONT_FAMILIES.has(element.fontFamily) ? element.fontFamily : DEFAULT_FONT_FAMILY}`,
        "line-height:1.5",
        `text-align:${["left", "center", "right"].includes(element.textAlign) ? element.textAlign : "left"}`,
        `border-radius:${element.type === "ellipse" ? "50%" : "5px"}`
    ];

    if (isShape) {
        styles.push(`background-color:${safeColor(element.fillColor, "transparent")}`);
        styles.push(`border-color:${safeColor(element.borderColor, "#315B7D")}`);
        styles.push(`border-width:${element.borderEnabled === false ? 0 : safeNumber(element.borderWidth, 1, 20)}px`);
        styles.push("border-style:solid");
    }

    if (isLine) styles.push(`background-color:${safeColor(element.color, "#1F2937")}`);
    return styles.join(";");
};

const resolveDynamicValue = (dataContext, path) => {
    if (!dataContext || typeof dataContext !== "object" || !path) return { found: false };

    if (Object.prototype.hasOwnProperty.call(dataContext, path)) {
        const directValue = dataContext[path];
        return directValue === null || directValue === undefined || directValue === ""
            ? { found: false }
            : { found: true, value: directValue };
    }

    const segments = path.split(".").filter(Boolean);
    if (segments.length === 0) return { found: false };

    let values = [dataContext];
    for (const segment of segments) {
        values = values.flatMap(value => {
            const candidates = Array.isArray(value) ? value : [value];
            return candidates
                .filter(candidate => candidate !== null
                    && typeof candidate === "object"
                    && Object.prototype.hasOwnProperty.call(candidate, segment))
                .map(candidate => candidate[segment]);
        });
        if (values.length === 0) return { found: false };
    }

    const value = values.length === 1 ? values[0] : values;
    return value === null || value === undefined || value === ""
        ? { found: false }
        : { found: true, value };
};

const formatDynamicValue = value => {
    const values = (Array.isArray(value) ? value.flat(Infinity) : [value])
        .filter(item => item !== null && item !== undefined && item !== "");
    return values
        .map(item => typeof item === "object" ? JSON.stringify(item) : String(item))
        .join(", ");
};

const renderElement = (element, layerIndex, dataContext, unresolvedFields) => {
    const style = elementStyle(element, layerIndex);
    const open = `<div class="certificate-element element-${escapeHtml(element.type)}" data-element-id="${escapeHtml(element.id)}" data-layer-index="${layerIndex}" style="${style}">`;
    const placeholderStyle = "display:flex;width:100%;height:100%;align-items:center;justify-content:center;border:1px dashed #94a3b8;color:#64748b;font:12px Arial,sans-serif";

    switch (element.type) {
        case "text":
            return `${open}<span class="element-text" style="display:block;width:100%;white-space:pre-wrap;overflow-wrap:anywhere">${escapeHtml(element.text || "Nuevo texto")}</span></div>`;
        case "dynamic": {
            const fieldPath = element.variable || "campo";
            const resolved = resolveDynamicValue(dataContext, fieldPath);
            const text = resolved.found
                ? formatDynamicValue(resolved.value)
                : `{{${fieldPath}}}`;
            if (!resolved.found) unresolvedFields.add(fieldPath);
            return `${open}<span class="element-text" style="display:block;width:100%;white-space:pre-wrap;overflow-wrap:anywhere">${escapeHtml(text)}</span></div>`;
        }
        case "image":
            if (!element.imageSrc || !isAllowedImageSource(element.imageSrc) || element.imageLoadError) {
                return `${open}<div class="element-placeholder" style="${placeholderStyle}">Imagen</div></div>`;
            }
            return `${open}<img class="element-image" src="${escapeHtml(element.imageSrc)}" alt="${escapeHtml(element.imageAlt || "Imagen del certificado")}" style="display:block;width:100%;height:100%;object-fit:contain"></div>`;
        case "signature":
            return `${open}<div class="element-placeholder" style="${placeholderStyle}">Firma</div></div>`;
        case "qr":
            return `${open}<div class="element-placeholder" style="${placeholderStyle}">Código QR</div></div>`;
        case "line":
            return `${open}</div>`;
        case "rectangle":
        case "ellipse":
            return `${open}</div>`;
        case "checkbox": {
            const checked = Boolean(element.checked);
            const color = safeColor(element.color, "#1F2937");
            const markStyle = `display:inline-flex;width:18px;height:18px;flex:0 0 18px;align-items:center;justify-content:center;box-sizing:border-box;border:1.5px solid ${color};border-radius:3px;background:${checked ? color : "#FFFFFF"};color:#FFFFFF;font:bold 12px Arial,sans-serif`;
            return `${open}<div class="element-checkbox" role="checkbox" aria-checked="${checked}" style="display:flex;width:100%;height:100%;align-items:center;gap:9px"><span class="checkbox-mark${checked ? " checked" : ""}" style="${markStyle}">${checked ? "&#10003;" : ""}</span><span>${escapeHtml(element.text || "Opción verificada")}</span></div></div>`;
        }
        default:
            return "";
    }
};

const renderCertificateHtml = (sourceDocument, previewScale = null, options = {}) => {
    const document = normalizeDocument(sourceDocument);
    const texture = document.pageStyle.texture;
    const manifest = escapeJsonForHtml(document);
    const isPreview = previewScale !== null;
    const previewWidth = isPreview ? Number((PAGE_WIDTH * previewScale).toFixed(3)) : null;
    const previewHeight = isPreview ? Number((PAGE_HEIGHT * previewScale).toFixed(3)) : null;
    const unresolvedFields = new Set();
    const elements = document.elements
        .filter(element => element.visible !== false)
        .map((element, layerIndex) => renderElement(element, layerIndex, options.dataContext, unresolvedFields))
        .join("\n");
    if (options.strictDynamicFields && unresolvedFields.size > 0) {
        throw new Error(`Faltan valores para los campos dinámicos: ${Array.from(unresolvedFields).join(", ")}`);
    }
    const textureStyle = texture === "dots"
        ? "background-image:radial-gradient(rgba(100,116,139,.2) .8px,transparent .8px);background-size:12px 12px"
        : texture === "lines"
            ? "background-image:repeating-linear-gradient(0deg,transparent 0 23px,rgba(100,116,139,.12) 23px 24px)"
            : texture === "diagonal"
                ? "background-image:repeating-linear-gradient(45deg,transparent 0 7px,rgba(100,116,139,.1) 7px 8px)"
                : "";
    const previewStyles = isPreview
        ? `
    .certificate-preview-page { position: relative; flex: 0 0 auto; width: ${previewWidth}px; height: ${previewHeight}px; }
    .certificate-preview-page > .certificate-sheet { position: absolute; top: 0; left: 0; transform: scale(${previewScale}); transform-origin: top left; }
    @media screen and (max-width: 1024px) { body.certificate-preview-body { padding: 12px; } }
    @media print {
      body.certificate-preview-body { padding: 0; }
      .certificate-preview-page { width: 700px; height: 990px; }
      .certificate-preview-page > .certificate-sheet { position: relative; top: auto; left: auto; transform: none; }
    }`
        : "";
    const previewWrapperStart = isPreview ? '<div class="certificate-preview-page">' : "";
    const previewWrapperEnd = isPreview ? "</div>" : "";
    const bodyClass = isPreview ? ' class="certificate-preview-body"' : "";
    const sheetPositionStyle = isPreview ? "" : "position:relative;";

    return `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Certificado personalizado</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #e9eef2; }
    body { min-height: 100vh; display: flex; justify-content: center; align-items: flex-start; padding: 24px; font-family: ${DEFAULT_FONT_FAMILY}; line-height: 1.5; }
    .certificate-sheet { position: relative; flex: 0 0 auto; width: 700px; height: 990px; overflow: hidden; background-color: ${document.pageStyle.backgroundColor}; border: 1px solid #dde3ec; }
    .texture-dots { background-image: radial-gradient(rgba(100,116,139,.2) .8px, transparent .8px); background-size: 12px 12px; }
    .texture-lines { background-image: repeating-linear-gradient(0deg, transparent 0 23px, rgba(100,116,139,.12) 23px 24px); }
    .texture-diagonal { background-image: repeating-linear-gradient(45deg, transparent 0 7px, rgba(100,116,139,.1) 7px 8px); }
    .certificate-element { position: absolute; display: flex; align-items: center; border: 1px solid transparent; overflow: visible; white-space: normal; line-height: 1.5; }
    .element-text { display: block; width: 100%; white-space: pre-wrap; overflow-wrap: anywhere; }
    .element-image { display: block; width: 100%; height: 100%; object-fit: contain; }
    .element-shape, .element-line { width: 100%; height: 100%; box-sizing: border-box; }
    .element-placeholder { display: flex; width: 100%; height: 100%; align-items: center; justify-content: center; border: 1px dashed #94a3b8; color: #64748b; font: 12px Arial, sans-serif; }
    .element-checkbox { display: flex; width: 100%; align-items: center; gap: 9px; }
    .checkbox-mark { display: inline-flex; width: 18px; height: 18px; flex: 0 0 18px; align-items: center; justify-content: center; border: 1.5px solid currentColor; border-radius: 3px; background: #fff; color: #fff; font: bold 12px Arial, sans-serif; }
    @page { size: 700px 990px; margin: 0; }
    @media print { html, body { width: 700px; height: 990px; min-height: 0; background: #fff; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } .certificate-sheet { width: 700px; height: 990px; border: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    ${previewStyles}
  </style>
</head>
<body${bodyClass}>
    ${previewWrapperStart}<main class="certificate-sheet texture-${texture}" style="${sheetPositionStyle}box-sizing:border-box;width:700px;height:990px;margin:0;overflow:hidden;background-color:${document.pageStyle.backgroundColor};${textureStyle}" aria-label="Certificado">
    ${elements}
  </main>${previewWrapperEnd}
  <script id="certificate-document" type="application/json">${manifest}</script>
</body>
</html>`;
};

export const buildCertificateHtml = (sourceDocument, options = {}) => renderCertificateHtml(sourceDocument, null, options);

export const buildCertificatePreviewHtml = (sourceDocument, viewportWidth, options = {}) => {
    if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) {
        throw new RangeError("The preview viewport width must be a positive number.");
    }

    const availableWidth = Math.max(1, Math.min(PAGE_WIDTH, viewportWidth - 24));
    return renderCertificateHtml(sourceDocument, availableWidth / PAGE_WIDTH, options);
};

export const serializeCertificateDocument = sourceDocument => `${JSON.stringify(normalizeDocument(sourceDocument), null, 2)}\n`;

const downloadBlob = (contents, type, fileName, environment) => {
    const blob = new environment.Blob([contents], { type });
    const url = environment.URL.createObjectURL(blob);
    const link = environment.document.createElement("a");

    link.href = url;
    link.download = fileName;
    environment.document.body.appendChild(link);
    link.click();
    link.remove();
    environment.setTimeout(() => environment.URL.revokeObjectURL(url), 0);
};

export const downloadCertificateHtml = (sourceDocument, environment = window, options = {}) => {
    downloadBlob(
        buildCertificateHtml(sourceDocument, options),
        "text/html;charset=utf-8",
        "certificado-personalizado.html",
        environment
    );
};

export const downloadCertificateJson = (sourceDocument, environment = window) => {
    downloadBlob(
        serializeCertificateDocument(sourceDocument),
        "application/json;charset=utf-8",
        "certificado-personalizado.json",
        environment
    );
};

export const downloadCertificatePdf = async (sourceDocument, environment = window, options = {}) => {
    if (!sourceDocument) throw new Error("No se encontró el diseño del certificado.");

    const printWindow = environment.open("", "_blank");
    if (!printWindow) throw new Error("Permite las ventanas emergentes para imprimir el certificado.");

    printWindow.document.open();
    printWindow.document.write(buildCertificateHtml(sourceDocument, options));
    printWindow.document.close();

    const imagesReady = Array.from(printWindow.document.images).map(image => {
        if (image.complete) return Promise.resolve();
        return new Promise(resolve => {
            image.addEventListener("load", resolve, { once: true });
            image.addEventListener("error", resolve, { once: true });
        });
    });
    await Promise.all([printWindow.document.fonts?.ready, ...imagesReady]);

    printWindow.focus();
    printWindow.print();
};